import { useMemo, useRef, useState, type CSSProperties } from "react";
import { Button } from "@legends/ui";
import ConsumerWatch from "./ConsumerWatch";
import RecordingsOnDemand from "./RecordingsOnDemand";
import { AsyncState, formatDateTime, formatKind, formatPrice, isProrated, LegendBrandMark, proratedNote, type LoadStatus } from "./sections";
import { KIND_EYEBROW } from "./lobbyLabels";
import { detectSocialPlatform, SocialIcon } from "./socialIcons";
import PublicLegalFooter from "./PublicLegalFooter";
import type {
  CaveEvent,
  Legend,
  LiveBroadcast,
  LobbyAsset,
  LobbyDocument,
  LobbyLinkedCardsContent,
  LobbyModule,
  LobbyPartnersContent,
  LobbyProgrammingContent,
  LobbySocialContent,
  LobbyStatsContent,
  LobbyStoryContent,
  TicketType,
} from "./api";

// The Cave Lobby renderer (ADR 0130, redesigned ADR 0132 to the Cave-Lobby-Mockup):
// the versioned, editor-authored storefront rendered in the Cave language. A fixed
// identity HERO (portrait + one explicit featured offer), an optional LIVE strip,
// a NEXT-in-the-Cave strip, then the authored modules on a 6-column grid, and a
// dependable Tickets & access route. It is sized by CONTAINER width (container
// queries in index.css) so the same markup reflows in the full-width storefront, a
// phone, and the editor preview frames. Commerce (Buy / Watch) is owned by the
// caller; this is presentation over real legend + cave data.

/** id → resolved asset lookup (server-resolved URLs, never client-supplied). */
type AssetMap = Record<string, LobbyAsset>;

export interface LobbyRendererProps {
  doc: LobbyDocument;
  caveName: string;
  legend: Legend | null;
  caveId: string;
  assets?: AssetMap;
  tickets: TicketType[];
  live: LiveBroadcast[];
  events: CaveEvent[];
  eventsStatus: LoadStatus;
  onRetryEvents?: () => void;
  owned: Set<string>;
  acquired: Set<string>;
  /** The fan holds VIP all-access (LSC-549): tickets reflect access, not a Buy nag. */
  vip?: boolean;
  signedIn: boolean | null;
  busyId: string | null;
  watching: string | null;
  onBuy: (t: TicketType) => void;
  onWatch: (broadcastId: string) => void;
  onStopWatch: () => void;
  onSignIn?: () => void;
  /** Editor preview only: surface the structural blocks (hero offer, Tickets &
   *  access) even when unpopulated — with a prompt — so the operator sees where
   *  they go. Never set on the public storefront (which stays data-clean). */
  editorPreview?: boolean;
}


const MON = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

// The module kinds this build knows how to render (LSC-490 forward-compat) — derived
// from the eyebrow map so it can't drift from LobbyModuleKind / ModuleBody's switch. A
// future document (a higher schemaVersion) may carry a kind this build predates; it's
// outside this set, so LobbyRenderer skips it cleanly (omits the card) rather than
// rendering an empty section. Additive kinds stay backward-compatible.
const KNOWN_MODULE_KINDS = new Set<string>(Object.keys(KIND_EYEBROW));

function spanToColumns(span: number): number {
  return span === 2 || span === 3 || span === 4 || span === 6 ? span : 6;
}
function contentOf<T>(m: LobbyModule): T {
  return (m.content ?? {}) as T;
}
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "LC";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function LobbyRenderer(props: LobbyRendererProps) {
  const {
    doc,
    caveName,
    legend,
    caveId,
    assets = {},
    tickets,
    live,
    events,
    eventsStatus,
    onRetryEvents,
    owned,
    acquired,
    vip = false,
    signedIn,
    busyId,
    watching,
    onBuy,
    onWatch,
    onStopWatch,
    onSignIn,
    editorPreview = false,
  } = props;

  const ticketsRef = useRef<HTMLDivElement | null>(null);
  const scrollToTickets = () => ticketsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  // "View schedule" targets the Programming/schedule module (LSC-298) — not Tickets,
  // which sits below it and made the scroll overshoot the schedule. Falls back to
  // Tickets when the lobby has no programming module.
  const programmingRef = useRef<HTMLDivElement | null>(null);
  const scrollToSchedule = () => (programmingRef.current ?? ticketsRef.current)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const profile = legend?.profile ?? null;
  const name = legend?.name ?? caveName;
  const initials = legend?.initials || initialsOf(name);
  const portrait = doc.hero.portraitAssetId ? assets[doc.hero.portraitAssetId] : undefined;

  const featured = useMemo(
    () => tickets.find((t) => t.id === doc.hero.featuredTicketTypeId) ?? null,
    [tickets, doc.hero.featuredTicketTypeId],
  );
  const featuredOwned = !!featured && (vip || owned.has(featured.id) || acquired.has(featured.id));
  // "VIP access ✓" when the fan has access via VIP (LSC-549) rather than a held ticket.
  const accessLabel = (held: boolean) => (vip && !held ? "VIP access ✓" : "Ticket owned ✓");
  // In the editor, keep the hero offer rail visible even before an offer is picked
  // (a prompt) so the operator sees the "Your seat in the Cave" slot.
  const showOffer = !!featured || (editorPreview && tickets.length > 0);

  // Render only modules whose `kind` this client knows, on top of the hidden filter
  // (LSC-490 forward-compat): a future document (a higher `schemaVersion`) may carry a
  // module kind this build predates. Skip it cleanly — omit the whole card — rather
  // than render an empty section (blank eyebrow, null body). Additive kinds stay
  // backward-compatible: the known modules render, the unknown one is silently dropped.
  const modules = doc.modules.filter((m) => !m.hidden && KNOWN_MODULE_KINDS.has(m.kind));
  // "Next in the Cave" is the nearest ELIGIBLE FUTURE occurrence (LSC-572): the events
  // feed is chronological (server-ordered by start date, never a custom/merchandising
  // order), and NEXT is the first one whose start is still in the future and that is NOT
  // currently live — a live occurrence belongs in LIVE NOW (LSC-297), and a started-but-
  // not-ended in-window CaveCast (ADR 0259, in the feed) is in the past so it is never
  // "next". `find` over the chronological feed therefore yields the nearest eligible one.
  const liveEventIds = useMemo(() => new Set(live.map((l) => l.eventId)), [live]);
  const nextEvent =
    events.find((e) => new Date(e.startsAt).getTime() > Date.now() && !liveEventIds.has(e.id)) ?? null;

  // Cave-level background (LSC-260 / LSC-647): a picked asset behind a design-system
  // dark scrim, filling the WHOLE public Cave page — the outer margins + behind the
  // header — not just the centered content column. It's a dedicated full-bleed layer
  // (`.lobby-bg`): position:fixed on the published storefront so it covers the generic
  // LSC cave photo (ADR 0018 `.cave-bg`) and stays put as the page scrolls, and
  // position:absolute inside the frame in the Designer preview so it fills the preview,
  // not the whole editor window (so the preview matches the published page). Empty =>
  // no layer => the generic cave photo shows as the default treatment (a fallback only,
  // never a frame around a configured bg). An opaque base keeps the page readable if
  // the image ever fails to load (and stops a transparent PNG leaking the generic one).
  const bgUrl = doc.background ? assets[doc.background]?.url : undefined;
  // Per-Cave accent (LSC-413): override the brand gold WITHIN the lobby scope so the
  // offer stripe, CTAs, and gold hairlines pick up the Cave's accent. A single
  // controlled value (validated #rrggbb server-side); the operator previews it live in
  // the Designer. Empty = the default LSC gold.
  const lobbyStyle: CSSProperties | undefined = doc.accent
    ? ({ "--gold": doc.accent } as CSSProperties)
    : undefined;

  return (
    <div
      className={editorPreview ? "lobby lobby--preview" : "lobby"}
      style={lobbyStyle}
      aria-label={`${name} — Cave lobby`}
    >
      {bgUrl && (
        <div
          className={editorPreview ? "lobby-bg lobby-bg--preview" : "lobby-bg"}
          aria-hidden="true"
          style={{
            // A consistent, strong protective scrim (LSC-265) over the picked image —
            // nearly flat (0.80→0.88) so protection never varies with the image's
            // brightness, keeping the Lobby typography + gold hairlines legible.
            backgroundImage: `linear-gradient(rgba(11,11,13,0.80), rgba(11,11,13,0.88)), url(${bgUrl})`,
          }}
        />
      )}
      {/* HERO */}
      <section className={showOffer ? "lobby-hero" : "lobby-hero lobby-hero--no-offer"}>
        <div className="lobby-portrait">
          {portrait ? (
            <img src={portrait.url} alt={portrait.alt} />
          ) : (
            <div className="lobby-portrait-inner">
              <span className="lobby-monogram">{initials}</span>
            </div>
          )}
        </div>
        <div>
          <span className="lobby-eyebrow">Welcome inside</span>
          <h1>{name}</h1>
          {profile?.sub && <p className="lobby-sub">{profile.sub}</p>}
          {(() => {
            // Prefer the document's own achievement pills (edited in the Designer,
            // LSC-294); fall back to the legend's profile tags when unset.
            const pills =
              doc.hero.achievements && doc.hero.achievements.length > 0 ? doc.hero.achievements : (profile?.tags ?? []);
            return pills.length > 0 ? (
              <div className="lobby-tags">
                {pills.slice(0, 8).map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            ) : null;
          })()}
        </div>
        {featured ? (
          <div className="lobby-offer">
            <span className="lobby-eyebrow">Your seat in the Cave</span>
            <h3>{featured.name}</h3>
            <div className="lobby-price">
              {isProrated(featured) && (
                <s style={{ opacity: 0.5, marginRight: 8, fontWeight: 400 }}>{formatPrice(featured.priceCents)}</s>
              )}
              {formatPrice(featured.proratedPriceCents)}
            </div>
            {proratedNote(featured) && <span className="lobby-offer-kind">{proratedNote(featured)}</span>}
            <span className="lobby-offer-kind">{formatKind(featured.kind)}</span>
            {featuredOwned ? (
              <span className="lobby-offer-kind" style={{ color: "var(--success-hi)" }}>
                {accessLabel(owned.has(featured.id) || acquired.has(featured.id))}
              </span>
            ) : (
              <Button
                variant="accent"
                aria-label={signedIn === false ? "Sign in to purchase" : `Explore ${featured.name}`}
                disabled={busyId === featured.id}
                onClick={() => (signedIn === false ? onSignIn?.() : onBuy(featured))}
              >
                {signedIn === false ? "Sign in to buy" : "Explore this ticket →"}
              </Button>
            )}
            {tickets.length > 1 && (
              <Button variant="muted" size="sm" onClick={scrollToTickets} aria-label="See all tickets">
                See all tickets
              </Button>
            )}
          </div>
        ) : showOffer ? (
          // Editor-only prompt: no featured offer picked yet, but the cave has
          // tickets — show the slot so the operator knows to fill it.
          <div className="lobby-offer">
            <span className="lobby-eyebrow">Your seat in the Cave</span>
            <h3 style={{ color: "var(--text-dim)" }}>No featured offer</h3>
            <p style={{ margin: 0, fontSize: "var(--fs-2xs)", color: "var(--text-dim)", lineHeight: "var(--lh-normal)" }}>
              Pick one in <strong>Hero → Featured offer</strong> to promote it here. Fans can still buy from Tickets &amp; access below.
            </p>
            <Button variant="muted" size="sm" onClick={scrollToTickets} aria-label="See all tickets">
              See all tickets
            </Button>
          </div>
        ) : null}
      </section>

      {/* LIVE — dependable, only when broadcasts actually are */}
      {watching ? (
        <ConsumerWatch broadcastId={watching} onClose={onStopWatch} onGetTicket={scrollToTickets} legend={legend ?? undefined} />
      ) : live.length > 0 ? (
        <section className="lobby-strip lobby-live-strip">
          <div>
            <span className="lobby-live-dot">● LIVE NOW</span>
            <h3 style={{ margin: "5px 0 0", fontSize: "var(--fs-lg)", color: "var(--text-hi)" }}>{live[0].title}</h3>
          </div>
          {signedIn === false ? (
            <Button variant="accent" aria-label={`Sign in to watch ${live[0].title}`} onClick={() => onSignIn?.()}>
              Sign in to watch
            </Button>
          ) : (
            <Button variant="accent" aria-label={`Watch ${live[0].title}`} onClick={() => onWatch(live[0].broadcastId)}>
              Watch live →
            </Button>
          )}
        </section>
      ) : null}

      {/* NEXT IN THE CAVE — the soonest real upcoming event */}
      {!watching && eventsStatus === "ready" && nextEvent && (
        <section className="lobby-strip">
          <div className="lobby-date-tile">
            <span>{MON[new Date(nextEvent.startsAt).getMonth()]}</span>
            <strong>{new Date(nextEvent.startsAt).getDate()}</strong>
          </div>
          <div className="lobby-next-copy">
            <span className="lobby-eyebrow">Next in the Cave</span>
            <h3>{nextEvent.title}</h3>
            <p>
              {formatDateTime(nextEvent.startsAt)} · {nextEvent.durationMinutes} min
            </p>
          </div>
          <Button variant="muted" size="sm" onClick={scrollToSchedule} aria-label="View schedule">
            View schedule →
          </Button>
        </section>
      )}

      {/* AUTHORED MODULES */}
      {modules.length > 0 && (
        <div className="lobby-grid">
          {modules.map((m) => (
            <section
              key={m.id}
              className="lobby-module"
              ref={m.kind === "programming" ? programmingRef : undefined}
              style={{ gridColumn: `span ${spanToColumns(m.span)}`, ...(m.kind === "programming" ? { scrollMarginTop: 12 } : {}) }}
            >
              <span className="lobby-eyebrow">{m.label || KIND_EYEBROW[m.kind]}</span>
              {m.title && <h2>{m.title}</h2>}
              <ModuleBody
                module={m}
                assets={assets}
                caveId={caveId}
                events={events}
                eventsStatus={eventsStatus}
                onRetryEvents={onRetryEvents}
                signedIn={signedIn}
                onSignIn={onSignIn}
                onSeeTickets={scrollToTickets}
              />
            </section>
          ))}
        </div>
      )}

      {/* TICKETS & ACCESS — the dependable route to the full catalog. Always shown
          in the editor (with a hint when empty) so the operator sees the block. */}
      {(tickets.length > 0 || editorPreview) && (
        <section className="lobby-module" ref={ticketsRef} style={{ scrollMarginTop: 12 }}>
          {/* A distinct eyebrow from the hero's "Your seat in the Cave" offer, so the
              full catalog reads as the catalog, not a duplicate of the featured one
              (LSC-266). The featured ticket is badged in the grid below. */}
          <span className="lobby-eyebrow">Every way in</span>
          <h2>Tickets &amp; access</h2>
          {tickets.length === 0 && (
            <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)" }}>
              No tickets yet — add ticket types in the <strong>Tickets</strong> tab and they appear here.
            </p>
          )}
          <div className="lobby-tickets">
            {tickets.map((t) => {
              const held = owned.has(t.id) || acquired.has(t.id);
              const isOwned = vip || held;
              return (
                <div key={t.id} className="lobby-ticket">
                  <div className="lobby-ticket-head">
                    <span className="lobby-ticket-name">{t.name}</span>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      {featured && t.id === featured.id && <span className="lobby-ticket-featured">Featured</span>}
                      <span className="lobby-offer-kind">{formatKind(t.kind)}</span>
                    </span>
                  </div>
                  <div className="lobby-ticket-foot">
                    <span className="lobby-ticket-price" title={proratedNote(t) || undefined}>
                      {isProrated(t) && (
                        <s style={{ opacity: 0.5, marginRight: 5, fontWeight: 400 }}>{formatPrice(t.priceCents)}</s>
                      )}
                      {formatPrice(t.proratedPriceCents)}
                    </span>
                    {isOwned ? (
                      <span className="lobby-offer-kind" style={{ color: "var(--success-hi)" }}>
                        {vip && !held ? "VIP access ✓" : "Owned ✓"}
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="accent"
                        aria-label={signedIn === false ? `Sign in to buy ${t.name}` : `Buy ${t.name}`}
                        disabled={busyId === t.id}
                        onClick={() => (signedIn === false ? onSignIn?.() : onBuy(t))}
                      >
                        {signedIn === false ? "Sign in to buy" : "Buy"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <footer className="lobby-footer">
        <span className="lobby-eyebrow lobby-brand">
          <LegendBrandMark size={20} />
          Legend Sports Cave
        </span>
        <span>Your team. Your Legend. Your Cave.</span>
        <PublicLegalFooter />
      </footer>
    </div>
  );
}

// ── module bodies ────────────────────────────────────────────────────────────

interface ModuleBodyProps {
  module: LobbyModule;
  assets: AssetMap;
  caveId: string;
  events: CaveEvent[];
  eventsStatus: LoadStatus;
  onRetryEvents?: () => void;
  signedIn: boolean | null;
  onSignIn?: () => void;
  onSeeTickets: () => void;
}

function ModuleBody(props: ModuleBodyProps) {
  const { module: m, assets } = props;
  switch (m.kind) {
    case "story": {
      const c = contentOf<LobbyStoryContent>(m);
      const img = c.imageAssetId ? assets[c.imageAssetId] : undefined;
      return (
        <>
          {img && <img src={img.url} alt={img.alt} style={{ width: "100%", borderRadius: "var(--radius-sm)", marginBottom: 12, objectFit: "cover" }} />}
          {c.body && <p style={{ whiteSpace: "pre-line" }}>{c.body}</p>}
          {c.caption && <div className="lobby-story-mark">{c.caption}</div>}
          {c.linkUrl && (
            <p style={{ marginTop: 12 }}>
              <a href={c.linkUrl} target="_blank" rel="noopener noreferrer nofollow" style={{ color: "var(--gold)", fontSize: "var(--fs-body-2)" }}>
                {c.linkLabel || "Learn more"} ↗
              </a>
            </p>
          )}
        </>
      );
    }
    case "stats": {
      const c = contentOf<LobbyStatsContent>(m);
      const items = c.items ?? [];
      if (items.length === 0) return null;
      return (
        <div className="lobby-stat-stack">
          {items.map((s, i) => (
            <div key={i}>
              <strong>{s.val}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      );
    }
    case "programming":
      return <ProgrammingBody {...props} />;
    case "linkedCards": {
      const c = contentOf<LobbyLinkedCardsContent>(m);
      const cards = c.cards ?? [];
      return (
        <div className="lobby-link-cards">
          {cards.map((card, i) => {
            const img = card.imageAssetId ? assets[card.imageAssetId] : undefined;
            return (
              <article key={i} className="lobby-link-card">
                {img && <img src={img.url} alt={img.alt} />}
                <h3>{card.title}</h3>
                {card.desc && <p>{card.desc}</p>}
                {card.actionUrl && (
                  <a href={card.actionUrl} target="_blank" rel="noopener noreferrer nofollow" style={{ color: "var(--gold)", fontSize: "var(--fs-body-2)" }}>
                    {card.actionLabel || "Learn more"} ↗
                  </a>
                )}
              </article>
            );
          })}
        </div>
      );
    }
    case "partners": {
      const c = contentOf<LobbyPartnersContent>(m);
      const entries = c.entries ?? [];
      return (
        <div className="lobby-partners">
          {entries.map((p, i) => {
            const logo = p.logoAssetId ? assets[p.logoAssetId] : undefined;
            const inner = (
              <>
                {logo && <img src={logo.url} alt={logo.alt} />}
                <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>{p.name}</span>
                {p.tier && <span className="lobby-offer-kind">{p.tier}</span>}
              </>
            );
            return p.url ? (
              <a key={i} className="lobby-partner" href={p.url} target="_blank" rel="noopener noreferrer nofollow">
                {inner}
              </a>
            ) : (
              <span key={i} className="lobby-partner">
                {inner}
              </span>
            );
          })}
        </div>
      );
    }
    case "social": {
      const c = contentOf<LobbySocialContent>(m);
      const links = c.links ?? [];
      return (
        <div className="lobby-socials">
          {links.map((l, i) => {
            // LSC-469 (ADR 0214): prepend a recognizable platform mark when the link
            // resolves to a known platform; unknown links keep the text-only treatment.
            const platform = detectSocialPlatform(l.url, l.platform);
            return (
              <a key={i} className="lobby-social" href={l.url} target="_blank" rel="noopener noreferrer nofollow">
                {platform && <SocialIcon platform={platform} />}
                {l.platform} ↗
              </a>
            );
          })}
        </div>
      );
    }
    default:
      return null;
  }
}

function ProgrammingBody(props: ModuleBodyProps) {
  const { module: m, caveId, events, eventsStatus, onRetryEvents, signedIn, onSignIn } = props;
  const c = contentOf<LobbyProgrammingContent>(m);
  const mode = c.mode === "upcoming" || c.mode === "replays" || c.mode === "both" ? c.mode : "both";
  const previewCount = c.previewCount >= 3 && c.previewCount <= 6 ? c.previewCount : 4;
  const hasUpcoming = mode === "upcoming" || mode === "both";
  const hasReplays = mode === "replays" || mode === "both";
  const [tab, setTab] = useState<"upcoming" | "replays">(hasUpcoming ? "upcoming" : "replays");
  // The "+N more" control EXPANDS the schedule in place (LSC-400) — it used to scroll to
  // Tickets, so the extra CaveCasts never appeared. Collapses back with "Show fewer".
  const [expanded, setExpanded] = useState(false);

  const pinned = c.eventIds ?? [];
  const feed = pinned.length > 0 ? (pinned.map((id) => events.find((e) => e.id === id)).filter(Boolean) as CaveEvent[]) : events;
  const preview = expanded ? feed : feed.slice(0, previewCount);
  const moreCount = feed.length - feed.slice(0, previewCount).length;

  return (
    <div>
      {hasUpcoming && hasReplays && (
        <div className="lobby-program-tabs" role="tablist" aria-label="Programming">
          <button type="button" aria-pressed={tab === "upcoming"} onClick={() => setTab("upcoming")}>
            Upcoming
          </button>
          <button type="button" aria-pressed={tab === "replays"} onClick={() => setTab("replays")}>
            Replays
          </button>
        </div>
      )}
      {hasUpcoming && (!hasReplays || tab === "upcoming") && (
        <div>
          {eventsStatus === "error" ? (
            <AsyncState status="error" errorText="Couldn't load the schedule." onRetry={onRetryEvents} compact />
          ) : eventsStatus === "loading" ? (
            <AsyncState status="loading" loadingText="Loading the schedule…" compact />
          ) : preview.length > 0 ? (
            <>
              <div className="lobby-program-list">
                {preview.map((e) => (
                  <div key={e.id} className="lobby-program-row">
                    <span className="lobby-program-icon">{new Date(e.startsAt).getDate()}</span>
                    <div>
                      <h3>{e.title}</h3>
                      <p>
                        {formatDateTime(e.startsAt)} · {e.durationMinutes} MIN
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {feed.length > previewCount && (
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpanded((v) => !v)}
                  className="lobby-eyebrow"
                  style={{ marginTop: 12, background: "none", border: "none", cursor: "pointer" }}
                >
                  {expanded ? "Show fewer ▴" : `+${moreCount} more ▾`}
                </button>
              )}
            </>
          ) : (
            <AsyncState status="empty" emptyText="No upcoming CaveCasts scheduled." compact />
          )}
        </div>
      )}
      {hasReplays && (!hasUpcoming || tab === "replays") && (
        <RecordingsOnDemand caveId={caveId} signedIn={signedIn} onSignIn={onSignIn} />
      )}
    </div>
  );
}
